import { apiClient } from './client';

export interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  isRead: boolean;
  createdAt: string;
  updatedAt: string;
  businessAccountId?: string | null;
  organizationId?: string | null;
  userId?: string;
}

export interface NotificationResponse {
  success?: boolean;
  data: Notification[];
  count?: number;
  unreadCount?: number;
  error?: string | null;
  totalCount?: number;
  filteredTotalCount?: number;
  hasMore?: boolean;
  message?: string;
}

export interface NotificationQuery {
  businessAccountId?: string;
  type?: string;
  isRead?: boolean;
  limit?: number;
  offset?: number;
  orderBy?: string;
  ascending?: boolean;
}

export const notificationsApi = {
  // Get all notifications for the authenticated user
  getAll: async (query: NotificationQuery = {}): Promise<NotificationResponse> => {
    const params = new URLSearchParams();
    
    if (query.businessAccountId) params.append('businessAccountId', query.businessAccountId);
    if (query.type) params.append('type', query.type);
    if (query.isRead !== undefined) params.append('isRead', query.isRead.toString());
    if (query.limit) params.append('limit', query.limit.toString());
    if (query.offset) params.append('offset', query.offset.toString());
    if (query.orderBy) params.append('orderBy', query.orderBy);
    if (query.ascending !== undefined) params.append('ascending', query.ascending.toString());

    const response = await apiClient.get(`/notifications?${params.toString()}`);
    return response;
  },

  // Get a specific notification
  getById: async (id: string): Promise<{ success: boolean; data: Notification }> => {
    const response = await apiClient.get(`/notifications/${id}`);
    return response.data;
  },

  // Mark notification as read
  markAsRead: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put(`/notifications/${id}/read`);
    return response.data;
  },

  // Mark all notifications as read
  markAllAsRead: async (query: { businessAccountId?: string; type?: string } = {}): Promise<{ success: boolean; message: string; updatedCount: number }> => {
    const params = new URLSearchParams();
    
    if (query.businessAccountId) params.append('businessAccountId', query.businessAccountId);
    if (query.type) params.append('type', query.type);

    const response = await apiClient.post(`/notifications/read-all?${params.toString()}`);
    return response.data;
  },

  // Delete notification
  delete: async (id: string): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.delete(`/notifications/${id}`);
    return response.data;
  },

  // Create notification (admin only)
  create: async (notification: {
    userId: string;
    businessAccountId?: string;
    type: string;
    title: string;
    message: string;
    data?: any;
  }): Promise<{ success: boolean; data: Notification }> => {
    const response = await apiClient.post('/notifications', notification);
    return response.data;
  },
};
